import { CircleAlert, CircleCheck, Info, type LucideIcon, X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { ToastText, ToastVariant } from "@/lib/toast";
import { cn } from "@/lib/utils";

/** Whether the toasts wait: the pointer or the focus is on them, or the tab is hidden. */
export const ToastPauseContext = createContext(false);

/**
 * Each kind's card: a white card for info; for success and error a soft tint
 * of the theme's green or red mixed into the card's colour (so it stays
 * opaque over the page, and follows dark mode), with a border of the same hue.
 */
const CARD: Record<ToastVariant, string> = {
  info: "bg-card border-border",
  success:
    "bg-[color-mix(in_oklab,var(--color-success)_10%,var(--color-card))] border-[color-mix(in_oklab,var(--color-success)_40%,var(--color-card))]",
  error:
    "bg-[color-mix(in_oklab,var(--color-destructive)_10%,var(--color-card))] border-[color-mix(in_oklab,var(--color-destructive)_40%,var(--color-card))]",
};

const ICON: Record<ToastVariant, { icon: LucideIcon; className: string }> = {
  info: { icon: Info, className: "text-primary" },
  success: { icon: CircleCheck, className: "text-success" },
  error: { icon: CircleAlert, className: "text-destructive" },
};

interface ToastCardProps {
  variant: ToastVariant;
  text: ToastText;
  /** How long it stays, in ms, not counting the time it waits. */
  durationMs: number;
  /** Closes it: its close button, Escape on the toast, or its time running out. */
  onDismiss: () => void;
}

/**
 * One toast: its kind's icon, the text in the dashboard's language, and a
 * close button. It closes by itself after `durationMs`, counting only the
 * time the toasts do not wait (`ToastPauseContext`).
 */
export function ToastCard({ variant, text, durationMs, onDismiss }: ToastCardProps) {
  const { t } = useTranslation();
  const paused = useContext(ToastPauseContext);
  const remaining = useRef(durationMs);
  const card = useRef<HTMLDivElement>(null);

  // With the focus on this toast, let go of it first, outside React's update, so
  // the toaster hears it leave and sonner hands it back to where it was before.
  const close = useCallback(() => {
    const toast = card.current?.closest<HTMLElement>("[data-sonner-toast]");
    const active = document.activeElement;
    if (toast && active instanceof HTMLElement && toast.contains(active)) {
      active.blur();
    }
    onDismiss();
  }, [onDismiss]);

  useEffect(() => {
    if (paused) {
      return;
    }
    const started = Date.now();
    const timer = setTimeout(close, remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current -= Date.now() - started;
    };
  }, [paused, close]);

  // Escape closes the toast with the focus anywhere on it: on the toast itself
  // (where Tab lands first) as well as on its close button.
  useEffect(() => {
    const toast = card.current?.closest<HTMLElement>("[data-sonner-toast]");
    if (!toast) {
      return;
    }
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      }
    };
    toast.addEventListener("keydown", onKeyDown);
    return () => toast.removeEventListener("keydown", onKeyDown);
  }, [close]);

  const { icon: Icon, className: iconClassName } = ICON[variant];
  // A stored key is any text the page checked with `i18n.exists`; values fill its placeholders.
  const words =
    typeof text === "function"
      ? text()
      : (t as unknown as (key: string, values?: Record<string, string>) => string)(
          text.key,
          text.values,
        );

  return (
    <div
      ref={card}
      data-toast={variant}
      className={cn(
        "text-foreground flex w-full items-start gap-3 rounded-lg border py-3 pr-3 pl-4 font-sans text-sm leading-snug shadow-md",
        CARD[variant],
      )}
    >
      <Icon aria-hidden="true" className={cn("mt-px size-5 shrink-0", iconClassName)} />
      <p className="min-w-0 flex-1 pt-0.5">{words}</p>
      <button
        type="button"
        onClick={close}
        aria-label={t("common.close")}
        className="text-muted hover:text-foreground hover:bg-foreground/5 focus-visible:ring-ring tap-target shrink-0 rounded-sm p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative"
      >
        <X aria-hidden="true" className="size-4" />
      </button>
    </div>
  );
}
